import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { NGXLogger } from 'ngx-logger';
import { exhaustMap, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

import { reportAction } from './reports.actions';
import { PdfReportService } from '../../services/pdf-report.service';
import { ReportService } from '../../services/report.service';

@Injectable()
export class ReportsEffects {
  constructor(
    private actions$: Actions,
    private logger: NGXLogger,
    private reportService: ReportService,
    private pdfReportService: PdfReportService
  ) {}

  salesPerformance$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(reportAction.salesPerformance),
      tap((action) => this.logger.debug('@Effect Export Sale Performance : ' + JSON.stringify(action.payload))),
      exhaustMap(({ payload }) => {
        return this.reportService.exportSalesPerformance(payload).pipe(
          map((response) => {
            return reportAction.exportMyReportResponse({
              isSuccess: true,
              response
            });
          }),
          catchError((error) => of(reportAction.exportReportFileError({ isSuccess: false, error })))
        );
      })
    );
  });

  salesTransaction$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(reportAction.salesTransaction),
      tap((action) => this.logger.debug('@Effect Export Sale Transaction : ' + JSON.stringify(action.payload))),
      exhaustMap(({ payload }) => {
        return this.reportService.exportSalesTransaction(payload).pipe(
          map((response) => {
            return reportAction.exportMyReportResponse({
              isSuccess: true,
              response: response
            });
          }),
          catchError((error) => of(reportAction.exportReportFileError({ isSuccess: false, error })))
        );
      })
    );
  });

  shiftRecord$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(reportAction.shiftRecord),
      tap((action) => this.logger.debug('@Effect Export Shift Record : ' + JSON.stringify(action.payload))),
      exhaustMap(({ payload }) => {
        return this.reportService.exportShiftSale(payload).pipe(
          map((response) => {
            return reportAction.exportMyReportResponse({
              isSuccess: true,
              response
            });
          }),
          catchError((error) => of(reportAction.exportReportFileError({ isSuccess: false, error })))
        );
      })
    );
  });

  countItemInTote$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(reportAction.countItemInTote),
      tap((action) => this.logger.debug('@Effect Export Count Item In Tote: ' + JSON.stringify(action.payload))),
      exhaustMap(({ payload }) => {
        return this.reportService.exportCountItemInTote(payload).pipe(
          map((response) => {
            return reportAction.exportMyReportResponse({
              isSuccess: true,
              response
            });
          }),
          catchError((error) => of(reportAction.exportReportFileError({ isSuccess: false, error })))
        );
      })
    );
  });

  storeOtherIncome$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(reportAction.storeOtherIncome),
      tap((action) => this.logger.debug('@Effect Export Store Other Income: ' + JSON.stringify(action.payload))),
      exhaustMap(({ payload }) => {
        return this.reportService.exportStoreOtherIncome(payload).pipe(
          map((response) => {
            return reportAction.exportMyReportResponse({
              isSuccess: true,
              response
            });
          }),
          catchError((error) => of(reportAction.exportReportFileError({ isSuccess: false, error })))
        );
      })
    );
  });

  materialRequestTransaction$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(reportAction.materialRequestTransaction),
      tap((action) =>
        this.logger.debug('@Effect Export Material Request Transaction: ' + JSON.stringify(action.payload))
      ),
      exhaustMap(({ payload }) => {
        return this.reportService.exportMaterialRequestTransaction(payload).pipe(
          map((response) => {
            return reportAction.exportMyReportResponse({
              isSuccess: true,
              response
            });
          }),
          catchError((error) => of(reportAction.exportReportFileError({ isSuccess: false, error })))
        );
      })
    );
  });

  reduceToClearSummary$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(reportAction.reduceToClearSummary),
      tap((action) => this.logger.debug('@Effect Export Reduce To Clear Summary: ' + JSON.stringify(action.payload))),
      exhaustMap(({ payload }) => {
        return this.reportService.exportReduceToClearSummary(payload).pipe(
          map((response) => {
            return reportAction.exportMyReportResponse({
              isSuccess: true,
              response
            });
          }),
          catchError((error) => of(reportAction.exportReportFileError({ isSuccess: false, error })))
        );
      })
    );
  });

  destroyTransaction$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(reportAction.destroyTransaction),
      tap((action) => this.logger.debug('@Effect Export Destroy Transaction: ' + JSON.stringify(action.payload))),
      exhaustMap(({ payload }) => {
        return this.reportService.exportDestroyTransaction(payload).pipe(
          map((response) => {
            return reportAction.exportMyReportResponse({
              isSuccess: true,
              response
            });
          }),
          catchError((error) => of(reportAction.exportReportFileError({ isSuccess: false, error })))
        );
      })
    );
  });

  storeExpense$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(reportAction.storeExpense),
      tap((action) => this.logger.debug('@Effect Export Store Expense: ' + JSON.stringify(action.payload))),
      exhaustMap(({ payload }) => {
        return this.reportService.exportStoreExpense(payload).pipe(
          map((response) => {
            return reportAction.exportMyReportResponse({
              isSuccess: true,
              response
            });
          }),
          catchError((error) => of(reportAction.exportReportFileError({ isSuccess: false, error })))
        );
      })
    );
  });

  reprintRecallPackedItems$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(reportAction.reprintRecallPackedItems),
      tap((action) => this.logger.debug('@Effect Export Reprint Recall Packed Items: ' + JSON.stringify(action))),
      exhaustMap(() => {
        return this.pdfReportService.getReprintRecallPackedItems().pipe(
          map((response) => {
            return reportAction.setPdfReport({
              payload: response
            });
          }),
          catchError((error) => of(reportAction.exportReportFileError({ isSuccess: false, error })))
        );
      })
    );
  });
  reprintRecallReturnReceipt$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(reportAction.reprintRecallReturnReceipt),
      tap((action) => this.logger.debug('@Effect Export Reprint Recall Return: ' + JSON.stringify(action))),
      exhaustMap(() => {
        return this.pdfReportService.getReprintRecallReturnReceipt().pipe(
          map((response) => {
            return reportAction.setPdfReport({
              payload: response
            });
          }),
          catchError((error) => of(reportAction.exportReportFileError({ isSuccess: false, error })))
        );
      })
    );
  });

  cafeStock$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(reportAction.cafeStock),
      tap((action) => this.logger.debug('@Effect Export Cafe Stock: ' + JSON.stringify(action))),
      exhaustMap(({ payload: { fileType, language, channel, storeCodes } }) => {
        const payloadSubmit = {
          productType: ['INVENTORY'],
          fileType,
          language,
          channel,
          location: storeCodes,
          storeCodes
        };
        return this.reportService.exportCafeStock(payloadSubmit).pipe(
          map((response) => {
            return reportAction.exportMyReportResponse({
              isSuccess: true,
              response
            });
          }),
          catchError((error) => of(reportAction.exportReportFileError({ isSuccess: false, error })))
        );
      })
    );
  });

  productTag$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(reportAction.productTag),
      tap((action) => this.logger.debug('@Effect Export product tag: ' + JSON.stringify(action))),
      exhaustMap(({ payload }) => {
        return this.reportService.exportProductTag(payload).pipe(
          map((response) => {
            return reportAction.exportMyReportResponse({
              isSuccess: true,
              response
            });
          }),
          catchError((error) => of(reportAction.exportReportFileError({ isSuccess: false, error })))
        );
      })
    );
  });
}
